import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import styled, { keyframes } from 'styled-components'
import { UploadCloud, FolderOpen, X } from 'lucide-react'

export interface AttachmentSourceModalProps {
  isOpen: boolean
  onClose: () => void
  onChooseUpload: () => void
  onChooseGallery: () => void
  title?: string
  description?: string
  uploadLabel?: string
  uploadDescription?: string
  galleryLabel?: string
  galleryDescription?: string
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  z-index: 10005;
  animation: ${fadeIn} 0.18s ease-out forwards;
`

const ModalBox = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 40px -8px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.04);
  z-index: 10006;
  padding: 24px 28px 28px 28px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  animation: ${scaleIn} 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
`

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
`

const Description = styled.p`
  margin: 0;
  font-size: 13px;
  color: #64748b;
`

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  transition: all 0.15s ease;

  &:hover {
    background: #f1f5f9;
    color: #334155;
  }
`

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const OptionCard = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  gap: 12px;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  box-sizing: border-box;

  &:hover {
    border-color: #0090d3;
    background: #f8fafc;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px -5px rgba(0, 144, 211, 0.12);
  }

  &:active {
    transform: translateY(0);
  }
`

const IconBadge = styled.div<{ $color: string; $bg: string }>`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  ${OptionCard}:hover & {
    transform: scale(1.08);
  }
`

const OptionLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`

const OptionSubtext = styled.span`
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.4;
`

export const AttachmentSourceModal: React.FC<AttachmentSourceModalProps> = ({
  isOpen,
  onClose,
  onChooseUpload,
  onChooseGallery,
  title = 'Adicionar Anexo',
  description = 'Como você deseja adicionar o arquivo?',
  uploadLabel = 'Novo Upload',
  uploadDescription = 'Enviar do computador ou dispositivo',
  galleryLabel = 'Escolher da Galeria',
  galleryDescription = 'Selecionar arquivos já salvos na empresa',
}) => {
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <>
      <Backdrop onClick={onClose} />
      <ModalBox role="dialog" aria-modal="true">
        <Header>
          <TitleArea>
            <Title>{title}</Title>
            <Description>{description}</Description>
          </TitleArea>
          <CloseButton type="button" onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </CloseButton>
        </Header>
        <OptionsGrid>
          <OptionCard
            type="button"
            onClick={() => {
              onChooseUpload()
              onClose()
            }}
          >
            <IconBadge $bg="#EFF6FF" $color="#0090D3">
              <UploadCloud size={26} />
            </IconBadge>
            <OptionLabel>{uploadLabel}</OptionLabel>
            <OptionSubtext>{uploadDescription}</OptionSubtext>
          </OptionCard>

          <OptionCard
            type="button"
            onClick={() => {
              onChooseGallery()
              onClose()
            }}
          >
            <IconBadge $bg="#F0FDF4" $color="#10B981">
              <FolderOpen size={26} />
            </IconBadge>
            <OptionLabel>{galleryLabel}</OptionLabel>
            <OptionSubtext>{galleryDescription}</OptionSubtext>
          </OptionCard>
        </OptionsGrid>
      </ModalBox>
    </>,
    document.body,
  )
}

export default AttachmentSourceModal
